import {FC} from "react";
import {useColorScheme} from "@mui/material/styles";
import {IconButton, IconButtonProps, Tooltip} from "@mui/material";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";

/**
 * Переключатель темы light/dark.
 *
 * Что было сломано:
 *   - useColorScheme().mode возвращает 'system' | 'light' | 'dark'.
 *     Старая логика `mode === 'dark' ? 'light' : 'dark'` на 'system'
 *     всегда переключала в dark — игнорируя реальную системную тему.
 *
 * Что сейчас:
 *   - systemMode даёт реальную тему когда mode === 'system'.
 *   - Иконка и переключение основаны на effectiveMode (что пользователь
 *     реально видит сейчас).
 *
 * Размер:
 *   - По умолчанию small (32×32) — для компактного подвала sidebar'а.
 *   - Может быть переопределён через props.
 */

const ColorModeIconDropdown: FC<Omit<IconButtonProps, "onClick">> = (props) => {
    const {mode, systemMode, setMode} = useColorScheme();

    // Эффективная тема — то, что пользователь реально видит на экране.
    // Если mode='system', берём системную; иначе — то что выставлено явно.
    const effectiveMode = mode === "system" ? systemMode : mode;
    const isDark = effectiveMode === "dark";

    const handleToggle = () => {
        // Переключаем между явными light/dark. Из 'system' выходим, чтобы
        // дальнейшие переключения работали предсказуемо.
        setMode(isDark ? "light" : "dark");
    };

    return (
        <Tooltip title={isDark ? "Светлая тема" : "Тёмная тема"}>
            <IconButton
                size="small"
                {...props}
                onClick={handleToggle}
                aria-label={isDark ? "Включить светлую тему" : "Включить тёмную тему"}
            >
                {isDark ? (
                    <LightModeOutlinedIcon fontSize="small"/>
                ) : (
                    <DarkModeOutlinedIcon fontSize="small"/>
                )}
            </IconButton>
        </Tooltip>
    );
};

export default ColorModeIconDropdown;